import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { fetchOrders, createOrder, deleteOrder, applyPromotionToOrder, reorderExistingOrder, voidOrder as voidOrderAPI } from '../services/api';
import { useAuth } from './AuthContext';

const OrderContext = createContext();

export const OrderProvider = ({ children }) => {
  const { isAuthenticated, user, getCurrentContext } = useAuth();
  const [orders, setOrders] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalValue, setTotalValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrdersData = useCallback(async (params = {}) => {
    // Enhanced authentication check
    if (!isAuthenticated()) {
      console.log('User not authenticated, skipping order fetch');
      setLoading(false);
      setOrders([]);
      setTotalOrders(0);
      return;
    }

    // Check if user data is properly loaded
    if (!user) {
      console.log('User data not loaded yet, skipping order fetch');
      setLoading(false);
      return;
    }

    // Check if user has required context (branch/department)
    const context = getCurrentContext();
    if (!context.branch_id && !user.is_superuser) {
      console.log('User has no branch context and is not superuser, skipping order fetch');
      setError('Unable to load orders: No branch context available. Please contact your administrator.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching orders with params:', params);
      console.log('User context:', context);
      
      const data = await fetchOrders({ 
        ...params, 
        include_items: true,
      });
      
      console.log('Raw data received from fetchOrders:', data);

      if (Array.isArray(data)) {
        console.log('Setting orders (array):', data);
        setOrders(data);
        setTotalOrders(data.length);
        // ✅ ADD THIS: Calculate total value from array
        const calculatedTotal = data.reduce((sum, order) => {
          const total = parseFloat(order.total_price) || parseFloat(order.total_with_tax) || parseFloat(order.subtotal) || 0;
          return sum + total;
        }, 0);
        setTotalValue(calculatedTotal);
      } else if (data && typeof data === 'object') {
        console.log('Setting orders (object):', data.results);
        console.log('Total orders count:', data.count);
        setOrders(data.results || []);
        setTotalOrders(data.count || 0);
        // ✅ ADD THIS: Use total_value from API if available, otherwise calculate
        if (data.total_value !== undefined) {
          setTotalValue(data.total_value);
        } else {
          const calculatedTotal = (data.results || []).reduce((sum, order) => {
            const total = parseFloat(order.total_price) || parseFloat(order.total_with_tax) || parseFloat(order.subtotal) || 0;
            return sum + total;
          }, 0);
          setTotalValue(calculatedTotal);
        }
      } else {
        console.warn('Unexpected data structure received:', data);
        setOrders([]);
        setTotalOrders(0);
        setTotalValue(0);  // ✅ ADD THIS LINE
      }

    } catch (error) {
      console.error('Error in fetchOrdersData:', error);
      
      // Enhanced error handling
      let errorMessage = 'An error occurred while fetching orders.';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error occurred. Please try again or contact support.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to access orders.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Your session has expired. Please log in again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      setOrders([]);
      setTotalOrders(0);
      setTotalValue(0);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, getCurrentContext]);

  useEffect(() => {
    console.log('OrderContext useEffect triggered');
    // Only fetch if user and context are properly loaded
    if (user && isAuthenticated()) {
      fetchOrdersData();
    }
  }, [fetchOrdersData, user]);

  const addOrder = async (orderData) => {
    try {
      console.log('Adding new order with data:', orderData);
      const newOrder = await createOrder(orderData);
      console.log('New order received from API:', newOrder);
      if (newOrder) {
        const formattedOrder = {
          ...newOrder,
          items: newOrder.items || [],
          total_price: newOrder.total_price || 0
        };
        console.log('Formatted new order:', formattedOrder);
        setOrders((prevOrders) => {
          const updatedOrders = [formattedOrder, ...prevOrders];
          console.log('Updated orders after adding:', updatedOrders);
          return updatedOrders;
        });
        setTotalOrders((prevTotal) => prevTotal + 1);
      } else {
        throw new Error('New order data is invalid');
      }
      return newOrder;
    } catch (error) {
      console.error('Error adding order:', error);
      setError(error.message || 'An error occurred while creating the order.');
      throw error;
    }
  };

  const deleteOrders = async (orderIds) => {
    try {
      console.log('Deleting orders with IDs:', orderIds);
      await Promise.all(orderIds.map(id => deleteOrder(id)));
      setOrders(prevOrders => {
        const updatedOrders = prevOrders.filter(order => !orderIds.includes(order.id));
        console.log('Updated orders after deletion:', updatedOrders);
        return updatedOrders;
      });
      setTotalOrders(prevTotal => {
        const newTotal = prevTotal - orderIds.length;
        console.log('Updated total orders after deletion:', newTotal);
        return newTotal;
      });
    } catch (error) {
      console.error('Error deleting orders:', error);
      setError(error.message || 'An error occurred while deleting orders.');
      throw error;
    }
  };

  const voidOrder = async (orderId, reason) => {
    try {
      console.log(`Voiding order with ID: ${orderId}`);
      const updatedOrder = await voidOrderAPI(orderId, reason);
      setOrders(prevOrders =>
        prevOrders.map(order => (order.id === orderId ? updatedOrder : order))
      );
      return updatedOrder;
    } catch (error) {
      console.error('Error voiding order:', error);
      setError(error.response?.data?.error || error.message || 'An error occurred while voiding the order.');
      throw error;
    }
  };

  const applyPromotion = async (orderId, promotionCode) => {
    try {
      console.log(`Applying promotion ${promotionCode} to order ${orderId}`);
      const updatedOrder = await applyPromotionToOrder(orderId, promotionCode);
      console.log('Updated order after applying promotion:', updatedOrder);
      setOrders(prevOrders => {
        const newOrders = prevOrders.map(order =>
          order.id === updatedOrder.id ? updatedOrder : order
        );
        console.log('Updated orders after applying promotion:', newOrders);
        return newOrders;
      });
      return updatedOrder;
    } catch (error) {
      console.error('Error applying promotion:', error);
      setError(error.message || 'An error occurred while applying the promotion.');
      throw error;
    }
  };

  const reorderOrder = async (orderId) => {
    try {
      console.log(`Reordering order with ID: ${orderId}`);
      const newOrder = await reorderExistingOrder(orderId);
      console.log('New order created from reorder:', newOrder);
      setOrders(prevOrders => {
        const updatedOrders = [newOrder, ...prevOrders];
        console.log('Updated orders after reordering:', updatedOrders);
        return updatedOrders;
      });
      setTotalOrders(prevTotal => {
        const newTotal = prevTotal + 1;
        console.log('Updated total orders after reordering:', newTotal);
        return newTotal;
      });
      return newOrder;
    } catch (error) {
      console.error('Error reordering:', error);
      setError(error.message || 'An error occurred while reordering.');
      throw error;
    }
  };

  return (
    <OrderContext.Provider value={{
      orders,
      totalOrders,
      totalValue,
      loading,
      error,
      fetchOrders: fetchOrdersData,
      addOrder,
      deleteOrders,
      voidOrder,
      applyPromotion,
      reorderOrder
    }}>
      {console.log('Current orders in context:', orders)}
      {children}
    </OrderContext.Provider>
  );
};

export const useOrders = () => {
  const context = useContext(OrderContext);
  if (context === undefined) {
    throw new Error('useOrders must be used within an OrderProvider');
  }
  return context;
};
